import { Autocomplete, Checkbox, TextField, createFilterOptions } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import React, { useEffect, useRef } from "react";
import { useSearchFormContext } from "./SearchForm";
import { useController } from "react-hook-form";
import useShiftRangeSelect from "./useShiftRangeSelect";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Limit displayed options for performance (infinite scrolling alternative)
// User types to filter — shows max 100 matches at a time
const defaultFilterOptions = createFilterOptions({
	limit: 100,
	matchFrom: 'any',
	stringify: (option) => option?.value || '',
});

function SearchDropdown({ label, dataLabel, useFirstAsDefault, multiple, commitTypedOnSearch = false }) {
	const { control, errors, criteriaData } = useSearchFormContext();
	const options = criteriaData?.[dataLabel] ? criteriaData?.[dataLabel] : [];
	const {
		field,
		fieldState: { invalid, isTouched, isDirty },
		formState: { touchedFields, dirtyFields },
	} = useController({ control, name: dataLabel, defaultValue: multiple ? [] : null });

	// For multi-select: default to "All" if available; for single: use first option
	// Only set defaults if no value already exists (e.g. restored from sessionStorage)
	useEffect(() => {
		if (useFirstAsDefault && options?.length > 0 && !field.value) {
			field.onChange(options[0]);
		} else if (multiple && options?.length > 0 && (!field.value || field.value.length === 0)) {
			const allOption = options.find(o => o.key === "All");
			if (allOption) {
				field.onChange([allOption]);
			}
		}
	}, [options]);

	// Applies the multi-select "All" toggle rules to a raw selection array and
	// commits it via field.onChange. Shared by the normal onChange path and the
	// Shift-range selection path so both behave identically.
	const commitMultiSelection = (v) => {
		// Handle "All" logic for multi-select
		const hasAll = v.some(item => (typeof item === 'string' ? item : item?.key) === 'All');
		const prevHadAll = field.value?.some?.(item => item?.key === 'All');

		if (v.length === 0) {
			// If user removes all, reset to "All"
			const allOption = options.find(o => o.key === "All");
			field.onChange(allOption ? [allOption] : []);
			return;
		}

		if (hasAll && !prevHadAll) {
			// User just selected "All" — keep only "All"
			const allOption = options.find(o => o.key === "All");
			field.onChange(allOption ? [allOption] : v);
			return;
		}

		if (hasAll && prevHadAll && v.length > 1) {
			// User selected another item while "All" was active — remove "All"
			const filtered = v.filter(item => (typeof item === 'string' ? item : item?.key) !== 'All');
			const mapped = filtered.map(item => typeof item === "string" ? { key: item, value: item } : item);
			field.onChange(mapped);
			return;
		}

		const newElem = v[v.length - 1];
		if (typeof newElem === "string")
			v[v.length - 1] = { key: newElem, value: newElem };
		field.onChange(v);
	};

	// Track the filtered options currently shown so Shift-range spans exactly
	// what the user sees (respects the type-to-search filter).
	const filteredOptionsRef = useRef(options);
	const isEqual = (a, b) => a?.value === b?.value;
	const { getAutocompleteProps, handleOptionClick } = useShiftRangeSelect({
		getOptions: () => filteredOptionsRef.current,
		getValue: () => field.value || [],
		onRangeSelect: (next) => commitMultiSelection(next),
		isEqual,
		// "All" is a sentinel — never include it in a Shift-range span.
		isRangeable: (option) => (typeof option === "string" ? option : option?.key) !== "All",
	});
	// Only enable range handlers in multi-select mode.
	const shiftProps = multiple ? getAutocompleteProps() : {};

	const commitTypedValue = (text) => {
		if (!multiple || !commitTypedOnSearch) return false;
		const typed = (text || "").trim().toLowerCase();
		if (!typed) return false;
		const option = options.find(
			(o) =>
				String(o?.value ?? "").toLowerCase() === typed ||
				String(o?.key ?? "").toLowerCase() === typed
		);
		if (!option) return false;
		const current = field.value || [];
		if (!current.some((item) => isEqual(item, option))) {
		commitMultiSelection([...current, option]);
		}
		return true;
	};

	return (
		<div className={`searchForm ${multiple && "multi-select-dropdown"}`}>
			<label htmlFor={dataLabel}>{label}</label>

			<Autocomplete
				disablePortal
				id={dataLabel}
				isOptionEqualToValue={(option, value) => option?.value === value?.value}
				options={options}
				multiple={multiple}
				disableCloseOnSelect={multiple}
				filterOptions={(opts, state) => {
					const base = options.length > 100 ? defaultFilterOptions(opts, state) : opts;
					// Remember what's visible for Shift-range math.
					if (multiple) filteredOptionsRef.current = base;
					return base;
				}}
				sx={{ width: 300 }}
				{...shiftProps}
				onChange={(e, v) => {
					if (multiple) {
						commitMultiSelection(v);
						return;
					}
					field.onChange(v);
				}}
				value={field.value}
				getOptionLabel={(option) => option?.value || ""}
				renderOption={multiple ? (props, option, { selected }) => {
					const { onClick, ...liProps } = props;
					return (
						<li
							{...liProps}
							key={option.key}
							onClick={(event) => {
								// Shift+click extends the range from the anchor; otherwise
								// fall through to MUI's default single-toggle.
								if (handleOptionClick(event, option)) return;
								onClick?.(event);
							}}
						>
							<Checkbox
								icon={checkboxIcon}
								checkedIcon={checkedIcon}
								style={{ marginRight: 8 }}
								checked={selected}
							/>
							{option?.value || ""}
						</li>
					);
				} : undefined}
				ListboxProps={{
					style: { maxHeight: 300 },
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={multiple ? `${label} (type to search)` : label}
						error={Boolean(errors[dataLabel])}
						onBlur={(e) => {
							commitTypedValue(e.target.value);
							field.onBlur(e);
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" && commitTypedValue(e.target.value)) {
								e.defaultMuiPrevented = true;
							}
						}}
						onPaste={(e) => {
							const pasted = e.clipboardData?.getData("text") || "";
							if (commitTypedValue(pasted)) {
								e.preventDefault();
							}
						}}
						name={field.name}
						sx={{ backgroundColor: "white" }}
						size="small"
					/>
				)}
			/>
		</div>
	);
}

export default SearchDropdown;
